import { Fragment, useEffect, useState } from 'react'
import { Stethoscope, User, Briefcase, Calendar, ListChecks, ClipboardList, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Validade dos Exames Médicos (WMOTEXM / RMOTEXA do ERP) — exames com validade dentro do período

interface LinhaExame { motcod: string; nome: string; cargo: string; exame: string; dataExame: string; meses: number | null; validade: string }
interface LinhaSintetica { motcod: string; nome: string; cargo: string; quantidade: number }
interface Relatorio { tipo: 'A' | 'S'; analitico: LinhaExame[]; sintetico: LinhaSintetica[]; totalExames: number; totalFuncionarios: number }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function RelatorioValidadeExames() {
  const { empresaCod } = useEmpresa()
  const hoje = isoLocal(new Date())
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [cargosSel, setCargosSel] = useState<string[]>([])
  const [dataIni, setDataIni] = useState(hoje)
  const [dataFim, setDataFim] = useState(hoje)
  const [tipo, setTipo] = useState<'A' | 'S'>('A')
  const [exames, setExames] = useState<{ cod: number; rot: string }[]>([])
  const [marcados, setMarcados] = useState<number[]>([])
  const [cargos, setCargos] = useState<{ cod: string; des: string }[]>([])
  const [res, setRes] = useState<Relatorio | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/funcionarios/relatorio/validade-exames/exames').then(r => { setExames(r.data); setMarcados(r.data.map((e: any) => e.cod)) }).catch(() => {})
    api.get('/funcionarios/cargos').then(r => setCargos(r.data.map((c: any) => ({ cod: c.catcod, des: c.catdes })))).catch(() => {})
  }, [])

  const params = () => ({ motcod: funcCod, cargos: cargosSel.join(','), inicio: dataIni, fim: dataFim, exames: marcados.join(','), tipo, empresa: empresaCod ?? 1 })
  const alternar = (cod: number) => setMarcados(m => (m.includes(cod) ? m.filter(x => x !== cod) : [...m, cod]))

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/validade-exames', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/validade-exames', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'validade-exames.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      let msg = formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }
  const btnMini: React.CSSProperties = { padding: '0.25rem 0.6rem', fontSize: '0.72rem' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 760, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Stethoscope size={18} color="#fff" />
            </span>
            <div>
              <h2>Validade dos Exames Médicos</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Exames dos funcionários que vencem dentro do período</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Funcionário" icon={User}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={funcCod} descValue={funcNome} onSelect={(cod, desc) => { setFuncCod(cod); setFuncNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {funcCod
                ? <button className="btn btn-secondary" style={btnMini} onClick={() => { setFuncCod(''); setFuncNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Cargo/Função" icon={Briefcase}>
            <SeletorMultiplo opcoes={cargos} selecionados={cargosSel} onChange={setCargosSel} placeholderBusca="Buscar cargo..." />
          </Linha>

          <Linha label="Validade" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>

          <Linha label="Analít./Sint." icon={ListChecks}>
            <select value={tipo} onChange={e => { setTipo(e.target.value as 'A' | 'S'); setRes(null) }} style={{ maxWidth: 160 }}>
              <option value="A">Analítico</option>
              <option value="S">Sintético</option>
            </select>
          </Linha>

          <Linha label="Exames" icon={ClipboardList}>
            <div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" style={btnMini} onClick={() => setMarcados(exames.map(e => e.cod))}>Marcar todos</button>
                <button className="btn btn-secondary" style={btnMini} onClick={() => setMarcados([])}>Desmarcar todos</button>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{marcados.length} de {exames.length} selecionados</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.25rem 0.8rem' }}>
                {exames.map(e => (
                  <label key={e.cod} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={marcados.includes(e.cod)} onChange={() => alternar(e.cod)} />
                    {e.rot}
                  </label>
                ))}
              </div>
            </div>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.totalExames === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum exame com validade no período.</div>}

            {res.totalExames > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  {res.tipo === 'A' ? (
                    <>
                      <thead>
                        <tr>
                          <th style={{ ...th, paddingLeft: '1.6rem' }}>Exame</th><th style={th}>Data Exame</th>
                          <th style={{ ...th, textAlign: 'right' }}>Meses</th><th style={th}>Validade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {res.analitico.map((l, i) => {
                          const novo = i === 0 || res.analitico[i - 1].motcod !== l.motcod
                          const qtd = novo ? res.sintetico.find(s => s.motcod === l.motcod)?.quantidade ?? 0 : 0
                          return (
                            <Fragment key={i}>
                              {novo && (
                                <tr style={{ background: '#e8f0fa' }}>
                                  <td colSpan={4} style={{ padding: '0.5rem 0.7rem', borderTop: i > 0 ? '2px solid #1b3f7b' : undefined, borderBottom: '1px solid #bfdbfe' }}>
                                    <span style={{ fontWeight: 800, color: '#1b3f7b', marginRight: 10 }}>{l.motcod}</span>
                                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{l.nome}</span>
                                    {l.cargo && <span style={{ color: '#64748b', fontSize: '0.78rem', marginLeft: 10 }}>· {l.cargo}</span>}
                                    <span style={{ float: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#1b3f7b' }}>{qtd} exame(s)</span>
                                  </td>
                                </tr>
                              )}
                              <tr style={{ background: '#fff' }}>
                                <td style={{ ...td, paddingLeft: '1.6rem' }}>{l.exame}</td>
                                <td style={td}>{dataBr(l.dataExame)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{l.meses ?? ''}</td>
                                <td style={{ ...td, fontWeight: 700, color: l.validade < hoje ? '#b91c1c' : '#1b3f7b' }}>{dataBr(l.validade)}</td>
                              </tr>
                            </Fragment>
                          )
                        })}
                      </tbody>
                    </>
                  ) : (
                    <>
                      <thead>
                        <tr><th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Cargo/Função</th><th style={{ ...th, textAlign: 'right' }}>Exames a vencer</th></tr>
                      </thead>
                      <tbody>
                        {res.sintetico.map((l, i) => (
                          <tr key={l.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                            <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td><td style={td}>{l.nome}</td><td style={td}>{l.cargo}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{l.quantidade}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <span>TOTAL DE EXAMES: {res.totalExames}</span>
              <span>TOTAL DE FUNCIONÁRIOS: {res.totalFuncionarios}</span>
            </div>
            {res.tipo === 'A' && res.totalExames > 0 && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Validade em vermelho = já vencida.</div>}
          </div>
        </div>
      )}
    </div>
  )
}
